import { Map as ImmutableMap } from 'immutable';
import Raven from 'raven-js';
import { useCallback } from 'react';
import unescapedText from 'I18n/utils/unescapedText';
import { useDeleteProductById } from 'commerce-products-lib/hooks/useDeleteProductById';
import { PRODUCT_TYPE_ID } from 'customer-data-objects/constants/ObjectTypeIds';
import { getProperty } from 'customer-data-objects/objects/ObjectAccessors';
import { FloatingAlertStore } from 'foundations-components/transitional/components/FloatingAlertList';
import { CloneProductActionContext } from '../contexts/CloneProductActionContext';
import { DeactivateProductActionContext } from '../contexts/DeactivateProductActionContext';
import { DeleteProductActionContext } from '../contexts/DeleteProductActionContext';
import { PreviewPanelCallbacksContext, usePreviewPanelCallbacks } from '../contexts/PreviewPanelCallbacksContext';
import { useProductData } from '../contexts/ProductDataContext';
import { useProductDeactivateModal } from '../contexts/ProductDeactivateModalContext';
import { useProductDeleteModal } from '../contexts/ProductDeleteModalContext';
import { useUpdateProductAction } from '../contexts/UpdateProductActionContext';
import { trackCloneProduct, trackDeactivateProduct, trackDeleteProduct } from '../utils/trackEvent';
import { ProductMutationsProvider } from './ProductMutationsProvider';
import { jsx as _jsx } from "react/jsx-runtime";
const ProductActionsProviderInner = ({
  children
}) => {
  var _product$objectId;
  const {
    product
  } = useProductData();
  const updateProduct = useUpdateProductAction();
  const {
    onObjectCreate,
    updatePreview,
    onObjectDelete,
    closeSidebar
  } = usePreviewPanelCallbacks();
  const cloneProduct = useCallback((createdProductId, modifiedPropertyNames) => {
    onObjectCreate({
      associationType: PRODUCT_TYPE_ID,
      objectId: createdProductId,
      addAnother: false
    });
    trackCloneProduct(modifiedPropertyNames);
    updatePreview(PRODUCT_TYPE_ID, createdProductId);
  }, [onObjectCreate, updatePreview]);
  const {
    setIsDeleteModalOpen
  } = useProductDeleteModal();
  const deleteProductFromApi = useDeleteProductById((_product$objectId = product === null || product === void 0 ? void 0 : product.objectId) !== null && _product$objectId !== void 0 ? _product$objectId : null);
  const deleteProduct = useCallback(() => {
    if (!(product !== null && product !== void 0 && product.objectId)) {
      const errorToThrow = new Error('Product objectId is not defined for deleteProduct. This should never happen.');
      Raven.captureException(errorToThrow);
      throw errorToThrow;
    }
    deleteProductFromApi().then(() => {
      setIsDeleteModalOpen(false);
      onObjectDelete({
        objectTypeId: PRODUCT_TYPE_ID,
        objectId: String(product.objectId)
      });
      trackDeleteProduct();
      closeSidebar();
    }).catch(error => {
      setIsDeleteModalOpen(false);
      Raven.captureException(error);
      FloatingAlertStore.addAlert({
        message: unescapedText('modal.delete.error.message'),
        titleText: unescapedText('modal.delete.error.title'),
        type: 'danger'
      });
    });
  }, [product, deleteProductFromApi, setIsDeleteModalOpen, onObjectDelete, closeSidebar]);
  const {
    closeDeactivateModal
  } = useProductDeactivateModal();
  const deactivateProduct = useCallback(() => {
    if (!(product !== null && product !== void 0 && product.objectId)) {
      const errorToThrow = new Error('Product objectId is not defined for deactivateProduct. This should never happen.');
      Raven.captureException(errorToThrow);
      throw errorToThrow;
    }
    closeDeactivateModal();
    updateProduct({
      hs_status: 'inactive'
    });
    trackDeactivateProduct();
  }, [product, closeDeactivateModal, updateProduct]);
  return /*#__PURE__*/_jsx(DeleteProductActionContext.Provider, {
    value: deleteProduct,
    children: /*#__PURE__*/_jsx(DeactivateProductActionContext.Provider, {
      value: deactivateProduct,
      children: /*#__PURE__*/_jsx(CloneProductActionContext.Provider, {
        value: cloneProduct,
        children: children
      })
    })
  });
};
export const ProductActionsProvider = ({
  onObjectCreate,
  onObjectUpdate,
  updatePreview,
  onObjectDelete,
  closeSidebar,
  children
}) => {
  const handleMutationSuccess = useCallback((updatedProduct, modifiedProperties) => {
    const properties = ImmutableMap(Array.from(modifiedProperties).map(propertyName => [propertyName, getProperty(updatedProduct, propertyName)]));
    onObjectUpdate({
      objectId: updatedProduct.objectId,
      objectType: PRODUCT_TYPE_ID,
      properties
    });
  }, [onObjectUpdate]);
  const previewPanelCallbacksValue = {
    onObjectCreate,
    onObjectUpdate,
    updatePreview,
    onObjectDelete,
    closeSidebar
  };
  return /*#__PURE__*/_jsx(PreviewPanelCallbacksContext.Provider, {
    value: previewPanelCallbacksValue,
    children: /*#__PURE__*/_jsx(ProductMutationsProvider, {
      onSuccess: handleMutationSuccess,
      children: /*#__PURE__*/_jsx(ProductActionsProviderInner, {
        children: children
      })
    })
  });
};